/**
 * Convert AWS service names to common abbreviations for pie chart labels
 */
export function getServiceAbbreviation(serviceName: string): string {
  const abbreviations: Record<string, string> = {
    // Compute
    'Amazon Elastic Compute Cloud': 'EC2',
    AmazonEC2: 'EC2',
    EC2: 'EC2',
    'AWS Lambda': 'Lambda',
    AWSLambda: 'Lambda',
    Lambda: 'Lambda',
    'Amazon Elastic Container Service': 'ECS',
    'Amazon ECS': 'ECS',
    AmazonECS: 'ECS',
    ECS: 'ECS',
    'Amazon Elastic Kubernetes Service': 'EKS',
    'Amazon EKS': 'EKS',
    AmazonEKS: 'EKS',
    EKS: 'EKS',
    'Amazon Elastic Container Service for Kubernetes': 'EKS',
    'Amazon EC2 Container Registry (ECR)': 'ECR',
    AmazonECR: 'ECR',
    ECR: 'ECR',
    'Amazon Elastic Container Registry': 'ECR',
    AmazonElasticContainerRegistry: 'ECR',
    'Elastic Container Registry': 'ECR',
    ElasticContainerRegistry: 'ECR',
    'Amazon Elastic Container Registry (ECR)': 'ECR',
    'AmazonElasticContainerRegistry(ECR)': 'ECR',
    'Elastic Container Registry (ECR)': 'ECR',
    'ElasticContainerRegistry(ECR)': 'ECR',

    // Storage
    'Amazon Simple Storage Service': 'S3',
    'Amazon S3': 'S3',
    AmazonS3: 'S3',
    S3: 'S3',
    'Amazon Elastic Block Store': 'EBS',
    'Amazon EBS': 'EBS',
    AmazonEBS: 'EBS',
    EBS: 'EBS',
    'Amazon Elastic File System': 'EFS',
    'Amazon EFS': 'EFS',
    AmazonEFS: 'EFS',
    EFS: 'EFS',

    // Database
    'Amazon Relational Database Service': 'RDS',
    'Amazon RDS': 'RDS',
    AmazonRDS: 'RDS',
    RDS: 'RDS',
    'Amazon DynamoDB': 'DynamoDB',
    AmazonDynamoDB: 'DynamoDB',
    DynamoDB: 'DynamoDB',
    'Amazon ElastiCache': 'ElastiCache',
    AmazonElastiCache: 'ElastiCache',
    ElastiCache: 'ElastiCache',
    'Amazon Redshift': 'Redshift',
    AmazonRedshift: 'Redshift',
    Redshift: 'Redshift',

    // Networking
    'Amazon CloudFront': 'CloudFront',
    AmazonCloudFront: 'CloudFront',
    CloudFront: 'CloudFront',
    'Amazon Route 53': 'Route 53',
    'Amazon Route53': 'Route 53',
    AmazonRoute53: 'Route 53',
    Route53: 'Route 53',
    'Amazon Virtual Private Cloud': 'VPC',
    'Amazon VPC': 'VPC',
    AmazonVPC: 'VPC',
    VPC: 'VPC',
    'Elastic Load Balancing': 'ELB',
    ElasticLoadBalancing: 'ELB',
    ELB: 'ELB',

    // Security & Identity
    'AWS Identity and Access Management': 'IAM',
    'AWS IAM': 'IAM',
    IAM: 'IAM',
    'AWS Key Management Service': 'KMS',
    'AWS KMS': 'KMS',
    awskms: 'KMS',
    KMS: 'KMS',
    'AWS Certificate Manager': 'ACM',
    'AWS ACM': 'ACM',
    ACM: 'ACM',

    // Monitoring & Management
    'Amazon CloudWatch': 'CloudWatch',
    AmazonCloudWatch: 'CloudWatch',
    CloudWatch: 'CloudWatch',
    'AWS CloudTrail': 'CloudTrail',
    AWSCloudTrail: 'CloudTrail',
    CloudTrail: 'CloudTrail',
    'AWS Config': 'Config',
    AWSConfig: 'Config',
    Config: 'Config',

    // Application Services
    'Amazon Simple Notification Service': 'SNS',
    'Amazon SNS': 'SNS',
    AmazonSNS: 'SNS',
    SNS: 'SNS',
    'Amazon Simple Queue Service': 'SQS',
    'Amazon SQS': 'SQS',
    AmazonSQS: 'SQS',
    SQS: 'SQS',
    'Amazon API Gateway': 'API Gateway',
    AmazonAPIGateway: 'API Gateway',
    'API Gateway': 'API Gateway',

    // Analytics
    'Amazon Kinesis': 'Kinesis',
    AmazonKinesis: 'Kinesis',
    Kinesis: 'Kinesis',
    'Amazon Elasticsearch Service': 'Elasticsearch',
    AmazonElasticsearch: 'Elasticsearch',
    Elasticsearch: 'Elasticsearch',

    // Machine Learning
    'Amazon SageMaker': 'SageMaker',
    AmazonSageMaker: 'SageMaker',
    SageMaker: 'SageMaker',

    // Security Services
    'Amazon GuardDuty': 'GuardDuty',
    AmazonGuardDuty: 'GuardDuty',
    GuardDuty: 'GuardDuty',
    'Amazon GuarDuty': 'GuardDuty', // Handle typo
    'AWS WAF': 'WAF',
    AWSWAF: 'WAF',
    awswaf: 'WAF',
    WAF: 'WAF',
    'AWS Shield': 'Shield',
    AWSShield: 'Shield',
    Shield: 'Shield',
    'Amazon Inspector': 'Inspector',
    AmazonInspector: 'Inspector',
    AmazonInspectorV2: 'Inspector',
    Inspector: 'Inspector',
    'AWS Secrets Manager': 'Secrets Mgr',
    AWSSecretsManager: 'Secrets Mgr',
    SecretsManager: 'Secrets Mgr',

    // Other
    'AWS Support': 'Support',
    AWSSupport: 'Support',
    AWSSupportBusiness: 'Support',
    'AWS Support (Business)': 'Support',
    Support: 'Support',
    'AWS Data Transfer': 'Data Transfer',
    AWSDataTransfer: 'Data Transfer',
    'Data Transfer': 'Data Transfer',

    // Load Balancing (additional entries)
    AWSELB: 'ELB',

    // Events & Messaging
    'Amazon EventBridge': 'EventBridge',
    AmazonEventBridge: 'EventBridge',
    AWSEvents: 'EventBridge',
    EventBridge: 'EventBridge',
    AWSQueueService: 'SQS',

    // Location
    'Amazon Location Service': 'Location',
    AmazonLocationService: 'Location',
    'Location Service': 'Location'
  }

  // Return abbreviation if found, otherwise return the original name (truncated if too long)
  const abbreviation = abbreviations[serviceName]
  if (abbreviation) {
    return abbreviation
  }

  // If no abbreviation found, return truncated version
  return serviceName.length > 12 ? serviceName.slice(0, 12) : serviceName
}
